import { endOfDay, startOfDay } from "date-fns";
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { reduxForm, SubmissionError, type Validator } from "redux-form";
import { CalloutIntent, DialogComponent, Modal } from "widgets";
import { useOverlay } from "widgets/hooks";

import { simpleDateValidation } from "@/app-utils/form.js";
import type { AuditEventType, AuditFilterOption, Playbook, Role, User } from "@/graphql.ts";
import { getUserDisplayName } from "@/modules/users/utils/user.ts";
import { downloadSystemAuditReport, loadAuditLogExportFilterOptions } from "@/redux/modules/data-export.js";
import { showNavbarElement } from "@/redux/modules/navbar.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import ReactSelectField, { ReactSelectReduxFormField } from "@/ui-modules/core/redux-form/ReactSelectField.tsx";

import { ReactDateSelectReduxFormField } from "../redux-form/ReactDateSelectorField.js";

const auditReportFormName = "downloadAuditReport";

enum FieldName {
  startDate = "startDate",
  endDate = "endDate",
  eventType = "eventType",
  owner = "owner",
  role = "role",
  playbook = "playbook",
}

interface IAuditReportFormState {
  [FieldName.startDate]: string;
  [FieldName.endDate]: string;
  [FieldName.eventType]: AuditEventType[];
  [FieldName.owner]: User[];
  [FieldName.role]: Role[];
  [FieldName.playbook]: Playbook[];
}

// we have no props, hence no need for the interface
export const AuditReportForm = reduxForm<IAuditReportFormState>({
  form: auditReportFormName,
})((props) => {
  const { reset, initialize, submitting, handleSubmit, error } = props;
  const dispatch = useDispatch<ReduxDispatch>();

  const loadingAuditExportFilterOptions = useSelector<IAppState, boolean | undefined>(
    (state) => state.dataExport.loadingAuditExportFilterOptions,
  );
  const auditExportFilterOptions = useSelector<IAppState, AuditFilterOption | undefined>(
    (state) => state.dataExport.auditExportFilterOptions,
  );

  // Dialog
  const isOpen = useSelector<IAppState, boolean>((state) => state.navBar.showAuditReportForm ?? false);
  const { overlayProps, state } = useOverlay();

  useEffect(() => {
    if (isOpen) {
      state.open();
      // load option data only when the dialog is opened
      dispatch(loadAuditLogExportFilterOptions());
    } else {
      state.close();
    }
    // state is unstable, so we do not want to observe it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    initialize({
      [FieldName.startDate]: startOfDay(new Date()).toISOString(),
      [FieldName.endDate]: endOfDay(new Date()).toISOString(),
      [FieldName.eventType]: undefined,
      [FieldName.owner]: undefined,
      [FieldName.role]: undefined,
      [FieldName.playbook]: undefined,
    });
    // should execute on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmitData = useCallback(
    (values: IAuditReportFormState): Promise<boolean> => {
      const startDate = values[FieldName.startDate];
      const endDate = values[FieldName.endDate];
      const eventType = values[FieldName.eventType]?.map((e) => e.id);
      const owner = values[FieldName.owner]?.map((o) => o.id);
      const playbook = values[FieldName.playbook]?.map((p) => p.id);
      const role = values[FieldName.role]?.map((r) => r.id) as number[];

      return dispatch(downloadSystemAuditReport(startDate, endDate, eventType, owner, playbook, role)).then(
        (fulfilled) => {
          reset();
          dispatch(showNavbarElement({ showAuditReportForm: false }));
          return fulfilled;
        },
        // the following will throw an error that redux-form understands as a submission error, with a general error that will be
        // available from props.error
        (rejected) => {
          throw new SubmissionError({ _error: rejected.message });
        },
      );
    },
    [reset, dispatch],
  );

  const getPlaybookLabel = useCallback((playbook: Playbook) => {
    return `${playbook.name}${playbook.nameSuffix ? " (" + playbook.nameSuffix + ")" : ""}`;
  }, []);

  return (
    <Modal state={state} {...overlayProps}>
      <DialogComponent
        title="Download an Audit Report"
        mainButton={{
          label: "Download",
          isDisabled: submitting,
          onPress: handleSubmit(handleSubmitData),
        }}
        closeFn={() => {
          reset();
          dispatch(showNavbarElement({ showAuditReportForm: false }));
        }}
        errorCallout={{ message: error, intent: CalloutIntent.Danger }}
      >
        <div>
          <p>An audit report will be generated for the dates specified and downloaded to your computer.</p>

          <div>
            <ReactDateSelectReduxFormField
              horizontalLayout={false}
              key={`audit-report-start-date`}
              name={FieldName.startDate}
              title={"Start date"}
              withTime={true}
              placeholder="Select the audit report start date/time..."
              validate={startDateValidation}
            />
            <ReactDateSelectReduxFormField
              horizontalLayout={false}
              key={`audit-report-end-date`}
              name={FieldName.endDate}
              title={"End date"}
              withTime={true}
              placeholder="Select the audit report end date/time..."
              validate={endDateValidation}
            />
          </div>
          <p>The following filter criteria can be used to further narrow down the exported audit log entries:</p>
          <div>
            <ReactSelectReduxFormField
              horizontalLayout={false}
              component={ReactSelectField}
              key={`audit-report-event-type`}
              name={FieldName.eventType}
              label={"Event type"}
              isMulti={true}
              placeholder="Optionally select event type(s)..."
              isClearable
              isSearchable
              closeMenuOnSelect={false}
              getOptionLabel={(option: AuditEventType) => option.name}
              isLoading={loadingAuditExportFilterOptions}
              getOptionValue={(option: AuditEventType) => option.id.toString()}
              options={auditExportFilterOptions?.eventTypes || []}
              formatOptionLabel={(option: AuditEventType) => option.name}
              isDisabled={loadingAuditExportFilterOptions}
            />
            <ReactSelectReduxFormField
              horizontalLayout={false}
              component={ReactSelectField}
              key={`audit-report-owner`}
              name={FieldName.owner}
              label={"User"}
              isMulti={true}
              placeholder="Optionally select user(s)..."
              isClearable
              isSearchable
              closeMenuOnSelect={false}
              getOptionLabel={(option: User) => getUserDisplayName(option, false, true)}
              isLoading={loadingAuditExportFilterOptions}
              getOptionValue={(option: User) => option.id.toString()}
              options={auditExportFilterOptions?.users || []}
              formatOptionLabel={(option: User) => getUserDisplayName(option, false, true)}
              isDisabled={loadingAuditExportFilterOptions}
            />
            <ReactSelectReduxFormField
              horizontalLayout={false}
              component={ReactSelectField}
              key={`audit-report-role`}
              name={FieldName.role}
              label={"Role"}
              isMulti={true}
              placeholder="Optionally select role(s)..."
              isClearable
              isSearchable
              closeMenuOnSelect={false}
              getOptionLabel={(option) => option.name}
              isLoading={loadingAuditExportFilterOptions}
              getOptionValue={(option: Role) => option.id?.toString() || ""}
              options={auditExportFilterOptions?.roles || []}
              formatOptionLabel={(option: Role) => option.name}
              isDisabled={loadingAuditExportFilterOptions}
            />
            <ReactSelectReduxFormField
              horizontalLayout={false}
              component={ReactSelectField}
              key={`audit-report-playbook`}
              name={FieldName.playbook}
              label={"Playbook"}
              isMulti={true}
              placeholder="Optionally select the playbook(s)..."
              isClearable
              isSearchable
              closeMenuOnSelect={false}
              getOptionLabel={getPlaybookLabel}
              isLoading={loadingAuditExportFilterOptions}
              getOptionValue={(option: Playbook) => option.id.toString()}
              options={auditExportFilterOptions?.playbooks || []}
              formatOptionLabel={getPlaybookLabel}
              isDisabled={loadingAuditExportFilterOptions}
            />
          </div>
        </div>
      </DialogComponent>
    </Modal>
  );
});

const startDateValidation: Validator = (value: Date, allValues?: IAuditReportFormState) => {
  const simpleValidation: string = simpleDateValidation(value);
  if (simpleValidation) {
    return simpleValidation;
  }

  const endDate = allValues?.[FieldName.endDate];
  if (endDate && new Date(endDate) < new Date(value)) {
    return "Start date must be BEFORE End date";
  }
  return undefined;
};

const endDateValidation: Validator = (value: Date, allValues?: IAuditReportFormState) => {
  const simpleValidation: string = simpleDateValidation(value);
  if (simpleValidation) {
    return simpleValidation;
  }

  const startDate = allValues?.[FieldName.startDate];
  if (startDate && new Date(startDate) > new Date(value)) {
    return "Start date must be BEFORE End date";
  }
  return undefined;
};
